import { NavLink, useNavigate } from "react-router-dom";
import {
    LineChart,
    Sprout,
    Users,
    BarChart3,
    Activity,
    LogOut,
    Leaf,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
    { to: "/dashboard", label: "Founders", icon: LineChart, enabled: true, key: "founders" },
    { to: "/suppliers", label: "Suppliers", icon: Sprout, enabled: true, key: "suppliers" },
    { to: "/users", label: "Users", icon: Users, enabled: true, key: "users" },
    { to: "/demand", label: "Demand", icon: BarChart3, enabled: true, key: "demand" },
    { to: "/health", label: "App Health", icon: Activity, enabled: true, key: "health" },
];

export default function Sidebar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const doLogout = () => {
        logout();
        navigate("/login", { replace: true });
    };

    return (
        <aside
            className="hidden md:flex flex-col w-64 shrink-0 h-screen border-r border-white/5 bg-[#070707] sticky top-0"
            data-testid="sidebar"
        >
            {/* Logo */}
            <div className="px-5 py-5 border-b border-white/5 flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-[#0A0A0A] border border-[#22C55E]/50 rounded-sm">
                    <Leaf size={18} className="text-[#22C55E]" />
                </div>
                <div className="leading-tight">
                    <div className="font-heading text-[#FAFAFA] text-sm font-bold tracking-wider">
                        HOMEGROWN APP
                    </div>
                    <div className="hg-eyebrow">Admin</div>
                </div>
            </div>

            {/* Status */}
            <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                <div className="hg-eyebrow">// Live</div>
                <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] hg-pulse-dot" />
                    <span className="font-mono-data text-[11px] text-[#A1A1AA]">
                        Connected
                    </span>
                </div>
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto py-3">
                <div className="hg-eyebrow px-5 pb-2">// Sections</div>
                {NAV.map(({ to, label, icon: Icon, enabled, key }) => (
                    <SidebarItem
                        key={key}
                        to={to}
                        label={label}
                        Icon={Icon}
                        enabled={enabled}
                        testid={`sidebar-nav-${key}`}
                    />
                ))}
            </nav>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-white/5">
                <div className="hg-eyebrow mb-1.5">// Operator</div>
                <div
                    className="font-mono-data text-[12px] text-[#FAFAFA] truncate"
                    data-testid="sidebar-user-email"
                >
                    {user?.email || "—"}
                </div>
                <button
                    onClick={doLogout}
                    className="mt-3 w-full flex items-center justify-between gap-2 px-3 py-2 border border-white/10 hover:border-[#ef4444]/50 hover:bg-[#ef4444]/5 rounded-sm transition-colors group"
                    data-testid="sidebar-logout-button"
                >
                    <span className="font-mono-data text-[11px] uppercase tracking-widest text-[#A1A1AA] group-hover:text-[#fecaca]">
                        Sign out
                    </span>
                    <LogOut size={13} className="text-[#A1A1AA] group-hover:text-[#fecaca]" />
                </button>
            </div>
        </aside>
    );
}

const SidebarItem = ({ to, label, Icon, enabled, testid }) => {
    if (!enabled) {
        return (
            <div
                className="relative px-5 py-2.5 flex items-center gap-3 cursor-not-allowed"
                data-testid={testid}
            >
                <Icon size={16} className="text-[#71717a]" />
                <span className="font-mono-data text-[13px] uppercase tracking-wider text-[#71717a]">
                    {label}
                </span>
                <span className="ml-auto font-mono-data text-[9px] uppercase tracking-widest text-[#22C55E]/60 border border-[#22C55E]/20 px-1.5 py-0.5 rounded-sm">
                    SOON
                </span>
            </div>
        );
    }

    return (
        <NavLink
            to={to}
            end
            className={({ isActive }) =>
                `relative px-5 py-2.5 flex items-center gap-3 group ${
                    isActive ? "hg-active-bar" : ""
                }`
            }
            data-testid={testid}
        >
            {({ isActive }) => (
                <>
                    <Icon
                        size={16}
                        className={
                            isActive
                                ? "text-[#22C55E]"
                                : "text-[#A1A1AA] group-hover:text-[#FAFAFA]"
                        }
                        style={
                            isActive
                                ? { filter: "drop-shadow(0 0 6px rgba(34,197,94,0.8))" }
                                : undefined
                        }
                    />
                    <span
                        className={`font-mono-data text-[13px] uppercase tracking-wider ${
                            isActive
                                ? "text-[#FAFAFA]"
                                : "text-[#A1A1AA] group-hover:text-[#FAFAFA]"
                        }`}
                    >
                        {label}
                    </span>
                    {isActive && (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#22C55E] hg-pulse-dot" />
                    )}
                </>
            )}
        </NavLink>
    );
};
